import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Building2,
  CalendarDays,
  Clock,
  Database,
  Lock,
  MapPin,
  ShieldCheck,
  Store,
  TrendingUp,
} from "lucide-react";
import { getTrackerPreview } from "@/lib/tracker.functions";
import { EVENT_STATUS_LABELS, EVENT_TYPE_LABELS, type TrackerEvent } from "@/lib/tracker/taxonomy";
import { Button } from "@/components/ui/button";
import { EditorialHero as StandardsHero } from "@/components/editorial-page-kit";
import { CALENDAR_BENEFIT_SHORT, CALENDAR_BENEFIT_TITLE } from "@/lib/membership/calendar-benefit";

const TITLE = "Deal & Expansion Tracker | Asian retail openings, deals and M&A";
const DESCRIPTION =
  "Track store openings, closings, market entries, acquisitions, investments and partnerships across Asian retail. Updated automatically from RetailNews Asia reporting.";

export const Route = createFileRoute("/tracker/")({
  loader: async () => getTrackerPreview(),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://retailnews.asia/tracker" }],
  }),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-serif text-2xl font-bold">The tracker is briefly unavailable</h1>
      <p className="mt-3 text-ink-muted">Please try again in a moment.</p>
    </main>
  ),
  notFoundComponent: () => <main className="px-4 py-24 text-center">Not found</main>,
  component: TrackerLanding,
});

function SectionHeading({ children, note }: { children: string; note?: string }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-hairline pb-3">
      <div>
        <p className="category-eyebrow">{children}</p>
        <div className="mt-2 h-1 w-16 rounded-full bg-gradient-to-r from-brand-deep via-brand to-brand-light" />
      </div>
      {note ? (
        <span className="font-sans text-[11px] uppercase tracking-wide text-ink-muted">{note}</span>
      ) : null}
    </div>
  );
}

function EventCard({ event }: { event: TrackerEvent }) {
  return (
    <li className="relative">
      <Link
        to="/tracker/$slug"
        params={{ slug: event.slug }}
        className="group flex h-full flex-col border border-hairline bg-card p-5 transition hover:border-brand hover:shadow-[0_18px_40px_-32px_rgba(0,0,0,0.6)]"
      >
        <div className="flex flex-wrap items-center gap-2 font-sans text-[10px] uppercase tracking-wide text-ink-muted">
          <span className="rounded-full bg-gradient-to-r from-brand-deep to-brand px-2.5 py-0.5 font-bold text-white">
            {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
          </span>
          <span className="rounded-full border border-hairline px-2 py-0.5">
            {EVENT_STATUS_LABELS[event.status] ?? event.status}
          </span>
          {event.announced_at ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3 text-brand" aria-hidden="true" />
              {event.announced_at}
            </span>
          ) : null}
        </div>
        <h3 className="mt-3 font-serif text-lg font-bold leading-snug group-hover:text-brand">
          {event.headline}
        </h3>
        {event.summary ? (
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-ink-muted">{event.summary}</p>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-hairline pt-3 text-xs text-ink-muted">
          {event.markets?.length ? (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
              {event.markets.join(", ")}
            </span>
          ) : null}
          <span className="ml-auto inline-flex items-center gap-1 font-sans font-bold uppercase tracking-[0.12em] text-brand">
            Open record
            <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" aria-hidden="true" />
          </span>
        </div>
      </Link>
    </li>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number | string;
  icon: typeof Store;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? "relative overflow-hidden border border-transparent bg-gradient-to-br from-brand-deep via-brand to-brand-light p-5 text-white"
          : "border border-hairline bg-card p-5"
      }
    >
      <Icon
        className={accent ? "h-4 w-4 text-white/80" : "h-4 w-4 text-brand"}
        aria-hidden="true"
      />
      <div className="mt-3 font-serif text-3xl font-bold tabular-nums leading-none">{value}</div>
      <div
        className={
          accent
            ? "mt-2 font-sans text-[10px] font-bold uppercase tracking-wide text-white/85"
            : "mt-2 font-sans text-[10px] font-bold uppercase tracking-wide text-ink-muted"
        }
      >
        {label}
      </div>
    </div>
  );
}

function RankList({
  title,
  icon: Icon,
  rows,
}: {
  title: string;
  icon: typeof MapPin;
  rows: Array<{ name: string; count: number }>;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-hairline bg-card p-5">
      <p className="category-eyebrow inline-flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
        {title}
      </p>
      <ul className="mt-4 space-y-3">
        {rows.map((row) => (
          <li key={row.name}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate font-medium">{row.name}</span>
              <span className="font-mono text-xs tabular-nums text-ink-muted">{row.count}</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-hairline">
              <div
                className="h-full rounded-full bg-gradient-to-r from-brand-deep via-brand to-brand-light"
                style={{ width: `${Math.round((row.count / max) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const EXEC_FEATURES = [
  "Full history and advanced filters",
  "Unlimited saved searches and alerts",
  "Company timelines and comparisons",
  "CSV exports",
  "Premium monthly reports",
  CALENDAR_BENEFIT_TITLE,
  "Self-service billing",
];

function TrackerLanding() {
  const initial = Route.useLoaderData();
  const { data } = useQuery({
    queryKey: ["tracker", "preview"],
    queryFn: () => getTrackerPreview(),
    initialData: initial,
    staleTime: 5 * 60_000,
  });

  const { events, stats, coverage } = data as unknown as {
    events: TrackerEvent[];
    stats: {
      today: number;
      week: number;
      month: number;
      openings: number;
      closings: number;
      topMarkets: Array<{ market: string; count: number }>;
      topCompanies: Array<{ company: string; count: number }>;
    };
    coverage: {
      from: string | null;
      processed: number;
      totalEstimate: number | null;
      complete: boolean;
    };
  };

  return (
    <>
      <StandardsHero
        eyebrow="Deal & Expansion Tracker"
        icon={<Database className="h-5 w-5" strokeWidth={2} />}
        title="Every move in Asian retail, as a structured record"
        lede="Store openings, closings, market entries, acquisitions, investments and partnerships — extracted from our own reporting, with the source article attached to every single entry."
        meta={[
          { label: "Events today", value: String(stats.today) },
          { label: "Last 7 days", value: String(stats.week) },
          {
            label: "Records processed",
            value: coverage.processed ? coverage.processed.toLocaleString("en-US") : "—",
          },
        ]}
      />

      <main className="container-editorial py-10">
        <section aria-label="Tracker activity" className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-4">
          <Stat label="Events today" value={stats.today} icon={CalendarDays} accent />
          <Stat label="Last 7 days" value={stats.week} icon={TrendingUp} />
          <Stat label="Last 30 days" value={stats.month} icon={Database} />
          <Stat
            label="Openings / closings"
            value={`${stats.openings} / ${stats.closings}`}
            icon={Store}
          />
        </section>

        <section className="mt-14" aria-labelledby="latest-events">
          <SectionHeading note="Free preview — last 7 days">Latest events</SectionHeading>
          {events.length ? (
            <ul className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </ul>
          ) : (
            <p className="border border-dashed border-hairline p-6 text-sm text-ink-muted">
              The first events are still being processed from our archive. Check back shortly.
            </p>
          )}
        </section>

        <section className="relative mt-14 overflow-hidden border border-hairline bg-gradient-to-br from-brand-deep via-brand to-brand-light p-8 text-white md:p-12">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl"
          />
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-center">
            <div>
              <p className="inline-flex items-center gap-2 font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-white/85">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                Executive benefit
              </p>
              <h2 className="mt-3 font-serif text-3xl font-bold leading-tight md:text-4xl">
                Unlock the complete tracker
              </h2>
              <p className="mt-4 max-w-2xl leading-relaxed text-white/85">
                Executive Members get the full event history, filtering by type, company, sector,
                market, date and deal size, company timelines, unlimited saved searches and alerts,
                CSV exports, the premium monthly report and a {CALENDAR_BENEFIT_SHORT.toLowerCase()}.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild variant="secondary">
                  <Link to="/membership">
                    Become an Executive Member — €250/year
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-white/50 bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to="/register" search={{ next: "/tracker" }}>Create a free account</Link>
                </Button>
              </div>
            </div>
            <ul className="grid gap-2 text-sm">
              {EXEC_FEATURES.map((item) => (
                <li key={item} className="flex items-start gap-2 border-b border-white/15 pb-2 last:border-0">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-white/80" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {stats.topMarkets.length || stats.topCompanies.length ? (
          <section className="mt-14" aria-labelledby="activity-ranking">
            <SectionHeading note="Rolling 30 days">Where the activity is</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              {stats.topMarkets.length ? (
                <RankList
                  title="Most active markets"
                  icon={MapPin}
                  rows={stats.topMarkets.map((r) => ({ name: r.market, count: r.count }))}
                />
              ) : null}
              {stats.topCompanies.length ? (
                <RankList
                  title="Most active companies"
                  icon={Building2}
                  rows={stats.topCompanies.map((r) => ({ name: r.company, count: r.count }))}
                />
              ) : null}
            </div>
          </section>
        ) : null}
      </main>

    </>
  );
}
